import type { DruckverlustAnlage, LueftungWegteilstrecke } from '../../api';
import { formatiereZahl, type BaumKnoten, type Fachstatus } from '../../muster';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TV6a - DIE REGELN DER SEITE „DRUCKVERLUST“ (ohne React, einzeln prüfbar)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Baum links (Anlage → Weg → Teilstrecke) und die Herkunft je
 * Eingangswert. Die Vorbehalte über den Zahlen kommen seit TV7a-2 (§22 c) aus
 * der Antwort des Berichts LUE-DRV (`LueftungBerichtsvorbehalte.tsx`), Satz
 * für Satz wie im PDF - bis dahin als Kopie hier. Bis TV6a standen sie in der Seite selbst,
 * neben rohen Tabellen und Inline-Stilen; hier stehen sie einmal, ohne
 * Kappung (der Baum des Musters zeigt alle 182 Verbraucher eines Wegs und
 * virtualisiert ab 200 sichtbaren Zeilen).
 */

/** Was am Baumknoten hängt: die Anlage, ein Weg oder eine Teilstrecke. */
export type Druckverlustknoten =
  | { art: 'anlage'; anlage: string }
  | { art: 'weg'; anlage: DruckverlustAnlage }
  | { art: 'teilstrecke'; anlage: DruckverlustAnlage; teilstrecke: LueftungWegteilstrecke };

/** Pa ganzzahlig wie im PDF LUE-DRV; ohne Wert „—“. */
export function pa(wert: number | null | undefined): string {
  return wert === null || wert === undefined || wert === 0 ? '—' : `${formatiereZahl(wert, 0)} Pa`;
}

/** Die Id eines Wegknotens - eindeutig im ganzen Baum. */
export function wegId(a: { anlage: string; weg: string }): string {
  return `w:${a.anlage}|${a.weg}`;
}

/**
 * Die oberen zwei Ebenen: Anlagen nach ihrem größten Druckverlust (absteigend),
 * darunter ihre Wege (Zuluft vor Abluft). Die Teilstrecken lädt der Baum je Weg
 * nach (`hatKinder`). Mit `nurVorbehalt` nur Wege mit fehlenden Beiwerten oder
 * auf vermaschtem Netz; mit `suche` nur Wege, deren Anlage, Weg, Kreis oder
 * maßgebender Raum den Text enthält.
 */
export function anlagenKnoten(
  anlagen: DruckverlustAnlage[],
  { suche = '', nurVorbehalt = false }: { suche?: string; nurVorbehalt?: boolean } = {},
): BaumKnoten<Druckverlustknoten>[] {
  const t = suche.trim().toLowerCase();
  const passt = (a: DruckverlustAnlage) => (!nurVorbehalt || a.fehlendeBeiwerte > 0 || a.istRingnetz)
    && (!t || a.anlage.toLowerCase().includes(t) || a.weg.toLowerCase().includes(t)
      || (a.kreis ?? '').toLowerCase().includes(t) || (a.massgebenderRaum ?? '').toLowerCase().includes(t));

  const gruppen = new Map<string, DruckverlustAnlage[]>();
  for (const a of anlagen.filter(passt)) {
    const liste = gruppen.get(a.anlage);
    if (liste) liste.push(a); else gruppen.set(a.anlage, [a]);
  }

  return [...gruppen.entries()]
    .map(([name, wege]) => ({
      name,
      wege: [...wege].sort((x, y) => (x.weg === 'zuluft' ? 0 : 1) - (y.weg === 'zuluft' ? 0 : 1)
        || x.weg.localeCompare(y.weg, 'de')),
      hoechster: Math.max(...wege.map((w) => w.druckverlustPa ?? 0)),
    }))
    .sort((x, y) => y.hoechster - x.hoechster || x.name.localeCompare(y.name, 'de', { numeric: true }))
    .map((g) => ({
      id: `a:${g.name}`,
      titel: g.name,
      daten: { art: 'anlage' as const, anlage: g.name },
      anzahl: g.wege.length,
      wert: pa(g.hoechster),
      kinder: g.wege.map((a) => ({
        id: wegId(a),
        titel: `${a.weg} · ${formatiereZahl(a.auslaesse, 0)} Auslässe`,
        daten: { art: 'weg' as const, anlage: a },
        hatKinder: true,
        wert: pa(a.druckverlustPa),
        hinweis: [
          a.istRingnetz ? 'vermascht' : null,
          a.fehlendeBeiwerte > 0 ? `${formatiereZahl(a.fehlendeBeiwerte, 0)} ohne Beiwert` : null,
        ].filter(Boolean).join(' · ') || undefined,
      })),
    }));
}

/**
 * Die dritte Ebene: alle Teilstrecken eines Wegs, die maßgebende zuerst, dann
 * nach Druckverlust absteigend - ohne Kappung.
 */
export function teilstreckenKnoten(
  anlage: DruckverlustAnlage, teilstrecken: LueftungWegteilstrecke[],
): BaumKnoten<Druckverlustknoten>[] {
  return [...teilstrecken]
    .sort((a, b) => Number(b.istMassgebend) - Number(a.istMassgebend)
      || (b.druckverlustPa ?? 0) - (a.druckverlustPa ?? 0) || a.nummer - b.nummer)
    .map((t) => ({
      id: `t:${t.id}`,
      titel: `${t.nummer} · ${t.raumnummer ?? 'ohne Raum'}`,
      daten: { art: 'teilstrecke' as const, anlage, teilstrecke: t },
      wert: pa(t.druckverlustPa),
      hinweis: [t.istMassgebend ? 'maßgebend' : null, t.ueberZiel ? 'über Zielpegel' : null]
        .filter(Boolean).join(' · ') || undefined,
    }));
}

/**
 * Die Herkunft eines Eingangswerts im Strang (Befund L-18) als Marke - mit Text,
 * nie nur als Farbe oder Tooltip (S1.10). Belegt (Modell, Datenblatt, gerechnet)
 * ist neutral, hergeleitet (Nachbarbauteil, Bauformregel) eine Warnung, kein
 * Wert ein Fehler: Sein Anteil fehlt in der Summe.
 */
export function postenherkunft(wert: string | null | undefined): Fachstatus {
  switch ((wert ?? '').trim().toLowerCase()) {
    case '': return { zustand: 'neutral', text: '—' };
    case 'modell': return { zustand: 'neutral', text: 'Modell' };
    case 'datenblatt': return { zustand: 'neutral', text: 'Datenblatt' };
    case 'gerechnet': return { zustand: 'neutral', text: 'gerechnet' };
    case 'nachbar': return { zustand: 'warnung', text: 'Nachbarbauteil' };
    case 'bauform': return { zustand: 'warnung', text: 'Bauformregel' };
    case 'keine': return { zustand: 'fehler', text: 'fehlt' };
    default: return { zustand: 'neutral', text: wert ?? '' };
  }
}

